import { useEffect, useState } from "react";
import api, { formatPrice } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { isLegacyStoreContent } from "@/lib/xplosivContent";

const empty = { code: "", description: "", discount_type: "percent", discount_value: 10, min_cart_value: 0, max_discount: null, usage_limit: null, is_active: true };

export default function AdminCoupons() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(empty);

    const load = async () => setItems((await api.get("/admin/coupons")).data.filter((item) => !isLegacyStoreContent(item)));
    useEffect(() => { load(); }, []);

    const save = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...form,
                code: form.code.toUpperCase(),
                discount_value: Number(form.discount_value),
                min_cart_value: Number(form.min_cart_value || 0),
                max_discount: form.max_discount ? Number(form.max_discount) : null,
                usage_limit: form.usage_limit ? Number(form.usage_limit) : null,
            };
            await api.post("/admin/coupons", payload);
            toast.success("Coupon created"); setForm(empty); load();
        } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    };
    const del = async (id) => { if (!window.confirm("Delete coupon?")) return; await api.delete(`/admin/coupons/${id}`); load(); };

    return (
        <div data-testid="admin-coupons-page">
            <h1 className="font-display text-4xl">Coupons</h1>
            <form onSubmit={save} className="mt-6">
                <Card className="rounded-[20px] border-[#E7DED2] bg-white/80">
                    <CardContent className="p-6 grid sm:grid-cols-2 gap-3">
                        <Fld label="Code"><Input required value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="XPEVENTWELCOME" /></Fld>
                        <Fld label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></Fld>
                        <Fld label="Type">
                            <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
                                <SelectTrigger className="rounded-2xl"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="percent">Percent</SelectItem><SelectItem value="flat">Flat (paise)</SelectItem></SelectContent>
                            </Select>
                        </Fld>
                        <Fld label={form.discount_type === "percent" ? "Discount %" : "Discount (paise)"}><Input type="number" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} /></Fld>
                        <Fld label="Min cart (paise)"><Input type="number" value={form.min_cart_value} onChange={e => setForm({ ...form, min_cart_value: e.target.value })} /></Fld>
                        <Fld label="Max discount (paise)"><Input type="number" value={form.max_discount || ""} onChange={e => setForm({ ...form, max_discount: e.target.value })} /></Fld>
                        <Fld label="Usage limit"><Input type="number" value={form.usage_limit || ""} onChange={e => setForm({ ...form, usage_limit: e.target.value })} /></Fld>
                        <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} /></div>
                        <div className="sm:col-span-2"><button type="submit" className="dw-btn-primary px-5 py-2 font-mono-ui" data-testid="admin-coupon-save">Create coupon</button></div>
                    </CardContent>
                </Card>
            </form>
            <Card className="mt-6 rounded-[20px] border-[#E7DED2] bg-white/80">
                <CardContent className="p-4">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs font-mono-ui text-muted-foreground border-b border-[#E7DED2]"><th className="py-2">Code</th><th>Type</th><th>Value</th><th>Min cart</th><th>Used</th><th>Status</th><th></th></tr></thead>
                        <tbody>
                            {items.map(c => (
                                <tr key={c.id} className="border-b border-[#F4EFE6]">
                                    <td className="py-3 font-mono font-semibold">{c.code}</td>
                                    <td>{c.discount_type}</td>
                                    <td>{c.discount_type === "percent" ? `${c.discount_value}%` : formatPrice(c.discount_value)}</td>
                                    <td>{formatPrice(c.min_cart_value)}</td>
                                    <td>{c.used_count}{c.usage_limit ? `/${c.usage_limit}` : ""}</td>
                                    <td>{c.is_active ? "Active" : "Inactive"}</td>
                                    <td><button onClick={() => del(c.id)} className="text-[#B42318]"><Trash2 className="h-4 w-4" /></button></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}

function Fld({ label, children }) { return <div><Label className="font-mono-ui mb-1.5 block">{label}</Label>{children}</div>; }
